import React from 'react';
import { useI18n } from './i18n';
import { helpOf, helpKeys, printedValues, termsToChips, PERSISTENCE_SECONDS } from './onavHelp';
import { COLOR_CATEGORIES, colorsOf, swatchBackground } from './onavColors';

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// The official colour chart "I colori del vino" (35 terms, 4 categories) with a swatch for each term
export function OnavColorChart() {
  const { t, lang } = useI18n();
  return (
    <div className="onav-colors">
      <h5>{t('onav.help.colors_title')}</h5>
      {lang !== 'it' && <small className="onav-help-note">{t('onav.help.colors_gloss_note')}</small>}
      {COLOR_CATEGORIES.map((category) => (
        <div key={category} className="onav-colors-group">
          <h6>{t(`onav.help.colors_${category}`)}</h6>
          <ul className="onav-colors-list">
            {colorsOf(category).map((c) => {
              const gloss = t(`onav.color.${c.key}`);
              const official = cap(c.term);
              return (
                <li key={`${category}-${c.key}`} className="onav-colors-item">
                  <span className="onav-swatch" style={{ background: swatchBackground(c) }} aria-hidden="true" />
                  <span className="onav-colors-term">
                    {official}
                    {lang !== 'it' && gloss.toLowerCase() !== c.term.toLowerCase() && <small> ({gloss})</small>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <small className="onav-help-source">{t('onav.help.colors_credit')}</small>
    </div>
  );
}

// Structured official help of one descriptor of the ONAV sheet: definition, terminology, notes, tips, the maximum of the current form
// and the values printed on it; the persistence row adds the reference in seconds and the hue row the colour chart.
export default function OnavHelpPanel({ field, max, id, onClose }) {
  const { t } = useI18n();
  const h = helpOf(field);
  const keys = helpKeys(field);
  if (!h || !keys) return null;
  const printed = printedValues(max);
  const levelLabel = (level) => t(`onav.help.level_${level}`);
  return (
    <div className="onav-help info-box" id={id} role="group" aria-label={t(`onav.p.${field}`)}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose && onClose(); } }}>
      <div className="onav-help-part">
        <h5>{t('onav.help.definition')}</h5>
        <p>{t(keys.definition)}</p>
      </div>

      {keys.terms && (
        <div className="onav-help-part">
          <h5>{t('onav.help.terms')}</h5>
          {h.terms === 'chips'
            ? <ul className="onav-help-chips">{termsToChips(t(keys.terms)).map((w, i) => <li key={i}>{w}</li>)}</ul>
            : <p>{t(keys.terms)}</p>}
        </div>
      )}

      {keys.termsRed && (
        <div className="onav-help-part">
          <h5>{t('onav.help.terms')}</h5>
          <p>{t(keys.termsRed)}</p>
          <p>{t(keys.termsWhite)}</p>
          <small className="onav-help-note">{t('onav.help.persistence_title')}: {['red', 'white'].map((kind) => (
            <span key={kind} className="onav-help-seconds">{t(`onav.help.colors_${kind === 'red' ? 'red' : 'white'}`)}: {PERSISTENCE_SECONDS[kind].map((r) => (
              `${levelLabel(r.level)} ${r.upTo !== undefined ? `≤ ${r.upTo}` : r.over !== undefined ? `> ${r.over}` : `${r.from}–${r.to}`}`)).join(' · ')}</span>
          ))}</small>
        </div>
      )}

      <div className="onav-help-part">
        <h5>{t('onav.help.note')}</h5>
        <p>{t(keys.note)}</p>
      </div>
      <div className="onav-help-part">
        <h5>{t('onav.help.tip')}</h5>
        <p>{t(keys.tip)}</p>
      </div>

      <div className="onav-help-scale">
        <span>{t('onav.help.max', { max })}</span>
        <span className="onav-help-printed">
          {t('onav.help.printed', { values: '' })}
          {printed.map((v) => <b key={v} className="onav-help-value">{v}</b>)}
        </span>
      </div>

      {h.colors && <OnavColorChart />}
      <small className="onav-help-source">{t('onav.help.source')}</small>
      <button type="button" className="reset-btn onav-help-close" onClick={onClose}>{t('onav.help.close')}</button>
    </div>
  );
}
